import type APIMessage from '~/types/APIMessage';

/**
 * This file contains the logic for classifying API errors into categories for observability purposes and remapping
 * error messages to more user-friendly messages for display in the web application.
 *
 * errorMessageMappings: This object maps by error "messages".
 * errorCodeMappings: This object maps by error "codes".
 *
 * Messages are processed first and then codes.
 *
 */

export enum APIErrorCategory {
  ADDRESS_VALIDATION = 'ADDRESS_VALIDATION',
  BATCH_API = 'BATCH_API',
  BATCH_TRANSACTION_API = 'BATCH_TRANSACTION_API',
  BILLING = 'BILLING',
  CARRIER = 'CARRIER',
  CUSTOMS = 'CUSTOMS',
  FRAUD = 'FRAUD',
  GENERAL = 'GENERAL',
  PHONE_EMAIL_NAME = 'PHONE_EMAIL_NAME',
  UNCLASSIFIED = 'UNCLASSIFIED',
}

interface ErrorMapping {
  errorCategory: APIErrorCategory;
  overrideMessage?: string;
}

const errorMessageMappings: { mapping: ErrorMapping; regex: string }[] = [
  {
    mapping: {
      errorCategory: APIErrorCategory.ADDRESS_VALIDATION,
    },
    regex: '^Recipient address invalid',
  },
  {
    mapping: {
      errorCategory: APIErrorCategory.ADDRESS_VALIDATION,
    },
    regex: '^Sender address is invalid',
  },
  {
    mapping: {
      errorCategory: APIErrorCategory.GENERAL,
    },
    regex: '^Shippo API could not process your request',
  },
  {
    mapping: {
      errorCategory: APIErrorCategory.PHONE_EMAIL_NAME,
      overrideMessage:
        'The sender name must include at least two words, each at least two characters long. Go back to the order and try again.',
    },
    regex: '^The address_from.name field should contain a full name, including at least two words',
  },
  {
    mapping: {
      errorCategory: APIErrorCategory.PHONE_EMAIL_NAME,
      overrideMessage:
        'The recipient name must include at least two words, each at least two characters long. Go back to the order and try again.',
    },
    regex: '^The address_to.name field should contain a full name, including at least two words',
  },
  {
    mapping: {
      errorCategory: APIErrorCategory.PHONE_EMAIL_NAME,
      overrideMessage:
        'Phone numbers must include 8-15 characters, using only numbers. Go back to the order and try again.',
    },
    regex: '^The address_to.phone field should contain between 8 and 15 numbers',
  },
  {
    mapping: {
      errorCategory: APIErrorCategory.CUSTOMS,
      overrideMessage:
        'For international shipments, include an AES/ITN number or an EEL/PFC exemption. Go back to the order and try again.',
    },
    regex: '^Attribute "customs_declaration.eel_pfc" must not be empty.',
  },
  {
    mapping: {
      errorCategory: APIErrorCategory.CUSTOMS,
      overrideMessage:
        "Item description must be 30 characters or less (ex. Men's Socks (M, 10-pack)",
    },
    regex:
      '^Attribute "customs_declaration.items\\[\\d+\\].description" must have \\d+ characters or less.$',
  },
  {
    mapping: {
      errorCategory: APIErrorCategory.GENERAL,
    },
    regex: '^We were not able to process your request\\. Please try again\\.$',
  },
  {
    mapping: {
      errorCategory: APIErrorCategory.CARRIER,
      overrideMessage:
        'Label text too long! Text on the label is limited to 15 characters or less per line. Go to Settings > Labels & packing slips to edit and try again.',
    },
    regex: '^Attribute "reference_1" must have 30 characters or less',
  },
  {
    mapping: {
      errorCategory: APIErrorCategory.CARRIER,
      overrideMessage:
        'Label text too long! Text on the label is limited to 15 characters or less per line. Go to Settings > Labels & packing slips to edit and try again.',
    },
    regex: '^Attribute "reference_2" must have 30 characters or less',
  },
  {
    mapping: {
      errorCategory: APIErrorCategory.BILLING,
      overrideMessage:
        'Your payment method was declined. Please update your payment method or add funds.',
    },
    regex:
      '^Payment for the label fail. You are required to have a valid payment method on file to purchase labels.',
  },
  {
    mapping: {
      errorCategory: APIErrorCategory.BILLING,
      overrideMessage:
        'Your payment method was declined. Please update your payment method or add funds.',
    },
    regex: '^Your request has failed due to a billing issue.',
  },
];

const errorCodeMappings: {
  [key: number | string]: ErrorMapping;
} = {
  10: {
    errorCategory: APIErrorCategory.CARRIER,
    // Various carriers: Colissimo, DHLGermany
  },
  1000: {
    errorCategory: APIErrorCategory.CARRIER,
    // Fedex
    // ERROR: Authentication Failed
  },
  1007: {
    errorCategory: APIErrorCategory.CARRIER,
    // DPDUK
  },
  1020: {
    errorCategory: APIErrorCategory.CARRIER,
    // DPDUK
  },
  1101: {
    errorCategory: APIErrorCategory.CARRIER,
    // DHLGermany
  },
  2033: {
    errorCategory: APIErrorCategory.CARRIER,
  },
  2039: {
    errorCategory: APIErrorCategory.CARRIER,
    // Fedex
    // The commercial invoice has been submitted electronically. You do not need to attach it to the parcel., ERROR: Incomplete commodity description 1
  },
  2208: {
    errorCategory: APIErrorCategory.CARRIER,
    // Fedex
  },
  2209: {
    errorCategory: APIErrorCategory.CARRIER,
    // Fedex
    // ERROR: Inactive customer account
  },
  2695: {
    errorCategory: APIErrorCategory.CARRIER,
    // CanadaPost
    // Only the following characters are accepted in Phone Number: ( ) - . p x, and + as the first character., Contact Phone number is a required field.
  },
  2700: {
    errorCategory: APIErrorCategory.CARRIER,
    // CanadaPost
    // Total Customs Weight must not be greater than the item weight.
  },
  3018: {
    errorCategory: APIErrorCategory.CARRIER,
    // Fedex
    // ERROR: Invalid Recipient StreetLine 1
  },
  3437: {
    errorCategory: APIErrorCategory.CARRIER,
    // ERROR: Printed Label of Origin - Invalid countryCode
    // FedEx
  },
  3907: {
    errorCategory: APIErrorCategory.CARRIER,
  },
  6535: {
    errorCategory: APIErrorCategory.CARRIER,
    // Fedex
    // ERROR: RequestedShipment Recipient contact - Phone Number is invalid
  },
  6541: {
    errorCategory: APIErrorCategory.CARRIER,
    // FedEx
    // ERROR: RequestedShipment Shipper contact - phoneNumber is required
  },
  6599: {
    errorCategory: APIErrorCategory.CARRIER,
    // Fedex
    // ERROR: LabelSpecification PrintedLabelOrigin contact - phoneNumber is required
  },
  6604: {
    errorCategory: APIErrorCategory.CARRIER,
    // Fedex
  },
  7252: {
    errorCategory: APIErrorCategory.CARRIER,
    // CanadaPost
    // There can only be 8 lines of goods for a shipment using the Tracked Packet service. Please reduce the number of goods or change the service.
  },
  7321: {
    errorCategory: APIErrorCategory.CARRIER,
    // CanadaPost
    // Only the following characters are accepted in Phone Number: ( ) - . p x, and + as the first character.
  },
  9174: {
    errorCategory: APIErrorCategory.CARRIER,
    // CanadaPost
    // To pay by credit card, a default payment card must be indicated on your Canada Post online profile. Please ensure an active card is stored as the default. Contract shippers may use "account" as an alternate method of payment.
  },
  45006: {
    errorCategory: APIErrorCategory.CARRIER,
    // AustraliaPost
    // Unable to pay for the order using the STORED_PAYMENT payment method. Merchant may need to update payment details.
  },
  '80040B1A': {
    errorCategory: APIErrorCategory.CARRIER,
    // USPS
    // Authorization failure. Perhaps username and/or password is incorrect.
  },
  120058: {
    errorCategory: APIErrorCategory.CARRIER,
    // UPS
    // UPS API did not respond. Please try again in a few minutes., Invalid Date. Changed to today's date, A Delivery Area surcharge has been added to the service cost.
  },
  120100: {
    errorCategory: APIErrorCategory.CARRIER,
  },
  120120: {
    errorCategory: APIErrorCategory.CARRIER,
    // UPS
    // UPS label service error: Severity: Hard, code: 120120: detail: The country associated with Shipper's ShipperNumber must be the same as the shipments Shipper's country.
  },
  120121: {
    errorCategory: APIErrorCategory.CARRIER,
    // UPS
    // UPS label service error: Severity: Hard, code: 120121: detail: The Shipper's shipper number cannot be used for the shipment
  },
  120207: {
    errorCategory: APIErrorCategory.CARRIER,
    // UPS
    // UPS label service error: Severity: Hard, code: 120207: detail: Missing/Invalid ShipTo PostalCode. Postal Code length must be 6.
  },
  120209: {
    errorCategory: APIErrorCategory.CARRIER,
    // UPS
    // UPS label service error: Severity: Hard, code: 120209: detail: Missing or invalid ship to phone number
  },
  120801: {
    errorCategory: APIErrorCategory.CARRIER,
    // UPS
    // UPS label service error: Severity: Hard, code: 120801: detail: Address Validation Error on Shipper address
  },
  120802: {
    errorCategory: APIErrorCategory.ADDRESS_VALIDATION,
  },
  121943: {
    errorCategory: APIErrorCategory.CARRIER,
    // UPS various
  },
  128048: {
    errorCategory: APIErrorCategory.CARRIER,
    // UPS
    // UPS label service error: Severity: Hard, code: 128048: detail: Invalid commodity code for product number 1. Valid length is 6 to 15 alphanumeric
  },
  220001: {
    errorCategory: APIErrorCategory.CARRIER,
  },
  420000: {
    errorCategory: APIErrorCategory.CARRIER,
  },
  420505: {
    errorCategory: APIErrorCategory.CARRIER,
  },
  420506: {
    errorCategory: APIErrorCategory.CARRIER,
  },
  9120119: {
    errorCategory: APIErrorCategory.CARRIER,
    // UPS
    // UPS label service error: Severity: Hard, code: 9120119: detail: Missing Package Level Reference Number value.
  },
  Billing: {
    errorCategory: APIErrorCategory.BILLING,
  },
  'CODAMT.LIMIT.REQUIRED': {
    errorCategory: APIErrorCategory.GENERAL,
    // FedEx
    // COD Amount must be between $1.00 and $9,999,999.99 code: CODAMT.LIMIT.REQUIRED
  },
  'CUSTOMER.ALCOHOLSPECIALSE': {
    errorCategory: APIErrorCategory.CARRIER,
    // FedEx
    // Customer not eligible for Alcohol special service code: CUSTOMER.ALCOHOLSPECIALSERVICETYPE.INELIGIBLE
  },
  'CUSTOMS.DESCRIPTION.INCOM': {
    errorCategory: APIErrorCategory.CUSTOMS,
    // FedEx
    // The description is incomplete and will not be accepted by customs.  Please enter a detailed description of the contents being shipped. code: CUSTOMS.DESCRIPTION.INCOMPLETE
  },
  Fraud: {
    errorCategory: APIErrorCategory.FRAUD,
    // Your account has been suspended. Please visit https://shippo.com/get-help to re-activate your account.
  },
  'ORGORDEST.SPECIALSERVICES': {
    errorCategory: APIErrorCategory.GENERAL,
    // FedEx
    // This special service type is not allowed for the origin/destination pair. Please update and try again code: ORGORDEST.SPECIALSERVICES.NOTALLOWED
  },
  'SERVICE.NOTELIGIBLE.FORAC': {
    errorCategory: APIErrorCategory.CARRIER,
    // FedEx
    // This Account is not eligible for  Smart Post Service. Please update and try again. code: SERVICE.NOTELIGIBLE.FORACCOUNT
  },
  'SHIPMENTVALIDATION.EEIEDI': {
    errorCategory: APIErrorCategory.CUSTOMS,
    // FedEx
    // The FTR Exemption or AES Citation you provided is not valid for EEI. code: SHIPMENTVALIDATION.EEIEDIT.ERROR
  },
  SV012a: {
    errorCategory: APIErrorCategory.CARRIER,
    // DHLExpress
  },
  Server: {
    errorCategory: APIErrorCategory.CARRIER,
    // CanadaPost various
  },
  SubmissionDateTooOld: {
    errorCategory: APIErrorCategory.GENERAL,
  },
  'Suspicious Activity': {
    errorCategory: APIErrorCategory.FRAUD,
  },
  TRANSACTION_REQUEST_FAILED: { errorCategory: APIErrorCategory.GENERAL },
  Timeout: {
    errorCategory: APIErrorCategory.CARRIER,
    // CanadaPost
  },
  carrier_request_failed: {
    errorCategory: APIErrorCategory.CARRIER,
    // General
  },
  carrier_timeout: {
    errorCategory: APIErrorCategory.CARRIER,
  },
  failed_address_validation: {
    errorCategory: APIErrorCategory.ADDRESS_VALIDATION,
  },
  ups_registration_error: {
    errorCategory: APIErrorCategory.CARRIER,
    // The UPS account is not yet registered.
  },
  user_input_problem: {
    errorCategory: APIErrorCategory.CARRIER,
    // Various carriers
  },
};

export const getMapping = (message: Pick<APIMessage, 'code' | 'text'>): ErrorMapping => {
  const code = message.code ?? 'Unknown';

  // Check mappings by error message first
  for (const mapping of errorMessageMappings)
    if (new RegExp(mapping.regex).test(message.text)) return mapping.mapping;

  // Check mappings by error code second if no message mappings
  if (errorCodeMappings[code] && errorCodeMappings[code].errorCategory)
    return errorCodeMappings[code];

  return {
    errorCategory: APIErrorCategory.UNCLASSIFIED,
  };
};

export const getErrorCategory = (messages: APIMessage[]): APIErrorCategory => {
  const mappings = messages.map((m) => getMapping(m));
  const withCategory = mappings.find((m) => m.errorCategory !== APIErrorCategory.UNCLASSIFIED);
  return withCategory?.errorCategory || APIErrorCategory.UNCLASSIFIED;
};

export const getMappedErrorMessages = (
  messages: Pick<APIMessage, 'code' | 'text'>[],
): APIMessage[] =>
  messages.map((m): APIMessage => {
    const mapping = getMapping(m);
    return {
      ...m,
      text: mapping.overrideMessage || m.text,
    } as APIMessage;
  });

export const applyMappedErrorsToOrderDict = (
  dict: Record<string, APIMessage[]>,
): Record<string, APIMessage[]> =>
  Object.keys(dict).reduce(
    (agg, key) => ({
      ...agg,
      [key]: getMappedErrorMessages(dict[key]),
    }),
    {},
  );

const getUniqueValues = <T>(items: T[], accessor: (item: T) => string | undefined): string => {
  const uniqueValues = new Set(
    items
      .map(accessor)
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0),
  );
  return Array.from(uniqueValues).join(', ');
};

interface ErrorLogMetadata {
  errorCategory: APIErrorCategory;
  errorCodes: string;
  errorCount: number;
  errorMessages: string;
  errorSources: string;
}

export const getErrorLogMetadata = (messages: APIMessage[]): ErrorLogMetadata => ({
  errorCategory: getErrorCategory(messages),
  errorCodes: getUniqueValues(messages, (message) => message.code),
  errorCount: messages.length,
  errorMessages: getUniqueValues(messages, (message) => message.text),
  errorSources: getUniqueValues(messages, (message) => message.source),
});
